import { useRef, useEffect, useCallback } from 'react';
import gsap from 'gsap';

/**
 * Hook that provides a GSAP timeline scoped to a component's lifecycle.
 * The timeline is automatically killed on unmount to prevent memory leaks.
 *
 * Usage:
 *   const { timelineRef, createTimeline } = useGsapTimeline();
 *
 *   useEffect(() => {
 *     const tl = createTimeline();
 *     tl.to(elementRef.current, { opacity: 1, duration: 0.5 });
 *   }, [trigger]);
 */
export function useGsapTimeline(defaults?: gsap.TimelineVars) {
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const createTimeline = useCallback(
    (vars?: gsap.TimelineVars) => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      timelineRef.current = gsap.timeline({ ...defaults, ...vars });
      return timelineRef.current;
    },
    [defaults]
  );

  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }
    };
  }, []);

  return { timelineRef, createTimeline };
}
